import {
  ArrowRightIcon,
  Box,
  Button,
  ExternalLinkIcon,
  InfoIcon,
  Link,
  RefreshIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import { StatusBadge } from 'components/SalaryComponents/components/StatusBadge';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { routePaths } from 'components/Routes/data';
import { TextWithDescription } from 'components/SalaryComponents/components/TextWithDescription';
import { StatusFilterKeyType } from 'components/SalaryComponents/components/filters/StatusFilter';
import {
  helpArticles,
  nonPayableBenefitsFiltersMap,
  salaryComponentParams,
} from 'components/SalaryComponents/constants';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import { getTableItemCountLimits } from 'components/SalaryComponents/utils';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';

import { NonPayableBenefitsDummyData } from 'components/SalaryComponents/__tests__/data';

const NonPayableBenefitsListView = ({
  onCountChange,
}: {
  onCountChange: (counts: Record<StatusFilterKeyType, number>) => void;
}) => {
  const history = useHistory();
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });
  // Using dummy data instead of API
  const nodes = NonPayableBenefitsDummyData;
  const isLoading = false;
  const isRefreshing = false;
  const isError = false;
  const { openDetailsView } = useDetailsView(salaryComponentParams.componentId);

  // Use useEffect to calculate and set counts only when needed
  useEffect(() => {
    if (onCountChange) {
      onCountChange({
        all: nodes.length,
        active: nodes.filter((node) => node.isActive).length,
        disabled: nodes.filter((node) => !node.isActive).length,
      });
    }
  }, [nodes, onCountChange]);

  const isTableEmpty = nodes.length === 0;
  const errorString = isError ? 'Failed to load data' : '';
  const { currentFilters, isAnyFilterApplied } = useFilters({
    filtersMap: nonPayableBenefitsFiltersMap,
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  return (
    <>
      <Table
        data={{ nodes }}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        pagination={
          !isTableEmpty ? (
            <TablePagination
              currentPage={currentPage}
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
              }}
              totalItemCount={nodes.length}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar title={getTableItemCountLimits(nodes.length, currentPage, itemsPerPage)}>
            <TableToolbarActions>
              <Button icon={RefreshIcon} variant={'tertiary'} onClick={() => {}} />
            </TableToolbarActions>
          </TableToolbar>
        }
        showBorderedCells={false}>
        {(salaryComponents) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text weight="semibold">Component Name</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Box display={'flex'} flexDirection={'row'} gap={'spacing.3'}>
                    <Text weight="semibold">Display Name</Text>
                    <Tooltip
                      placement="top"
                      content={`The name that will appear in employee's payslip and Salary Register`}>
                      <TooltipInteractiveWrapper>
                        <InfoIcon marginTop={'spacing.1'} />
                      </TooltipInteractiveWrapper>
                    </Tooltip>
                  </Box>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">Part of CTC</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold" marginX={'auto'}>
                    Status
                  </Text>
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {salaryComponents.map((salaryComponent) => {
                return (
                  <TableRow
                    key={salaryComponent.id}
                    item={salaryComponent}
                    onClick={() => openDetailsView(salaryComponent.id)}
                    hoverActions={
                      <Button
                        variant="tertiary"
                        color="primary"
                        size="medium"
                        icon={ArrowRightIcon}
                        iconPosition="right"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetailsView(salaryComponent.id);
                        }}
                        testID={`view-details-button-${salaryComponent.id}`}>
                        View Details
                      </Button>
                    }>
                    <TableCell>
                      <TextWithDescription componentName={salaryComponent.name} />
                    </TableCell>
                    <TableCell>
                      <Text>
                        {salaryComponent.settings.behaviour.displayBehaviour?.displayName ??
                          salaryComponent.name}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text>
                        {salaryComponent.settings.nonPayableBenefitsDetails.isPartOfCTC
                          ? 'Yes'
                          : 'No'}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Box marginX={'auto'}>
                        <StatusBadge isActive={salaryComponent.isActive} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>
      {isTableEmpty && !isLoading && !isRefreshing && !isError && (
        <EmptyListView
          title={
            isAnyFilterApplied
              ? 'No Search Results Found'
              : 'No Non-payable Benefits components created yet 🙈'
          }
          description={
            <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
              Click on Create Component button to create your first component
            </Text>
          }>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  history.push(routePaths.salaryComponents.create.nonPayableBenefits.root);
                }}
                isFullWidth>
                Create Non-payable Benefits Component
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
      {isError && (
        <EmptyListView
          title={`There was an error while fetching the data for ${
            currentFilters.status ?? 'all'
          } non-payable benefits components. `}
          description={errorString ? `Error: ${errorString}` : null}>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {}}
                isFullWidth
                iconPosition="left"
                icon={RefreshIcon}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
    </>
  );
};

export default NonPayableBenefitsListView;
